import { ERROR_CODES, type ErrorCode } from "@Common/error_codes";
import { usePublicMode } from "@Src/contexts/PublicModeContext";
import { Box, Card, CardContent, CardHeader, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { match } from "ts-pattern";

export const ErrorPage = () => {
	const { setIsPublic } = usePublicMode();
	const [searchParams] = useSearchParams();

	useEffect(() => {
		setIsPublic(true);
		return () => {
			setIsPublic(false);
		};
	}, [setIsPublic]);

	const errorMessage = searchParams.get("message");

	const { title, message, secondaryMessage } = useMemo(
		() =>
			match(errorMessage as ErrorCode)
				.with(ERROR_CODES.SUBSCRIPTION_APPLICATION_SUBMISSION_LIMIT, () => ({
					title: "Form Submission Limit Reached",
					message:
						"This form has reached its monthly submission limit. Please try again next month or contact the form owner.",
					secondaryMessage: null,
				}))
				.with(ERROR_CODES.SUBSCRIPTION_FORM_CREATION_LIMIT, () => ({
					title: "Form Creation Limit Reached",
					message:
						"You have reached your form creation limit. Please upgrade your plan to be able to create more forms.",
					secondaryMessage: null,
				}))
				.with(ERROR_CODES.LINK_INVALID, () => ({
					title: "Link unavailable",
					message: "This link is no longer valid or the form has been removed.",
					secondaryMessage:
						"Please contact the organisation that sent you this link.",
				}))
				.with(ERROR_CODES.LINK_ALREADY_USED, () => ({
					title: "Link Already Used",
					message: "This link has been used already.",
					secondaryMessage:
						"If you think it's a mistake, please contact the form creator.",
				}))
				.with(ERROR_CODES.MISSING_LINKEDIN_INFO, () => ({
					title: "LinkedIn Authentication Required",
					message:
						"This form requires LinkedIn authentication. Please sign in with your LinkedIn account to continue.",
					secondaryMessage: null,
				}))
				.with(ERROR_CODES.LINKEDIN_EMAIL_ALREADY_TAKEN, () => ({
					title: "Duplicate Submission Detected",
					message: "This LinkedIn identity has already responded to this form.",
					secondaryMessage:
						"If you think this is a mistake, please contact the form creator.",
				}))
				.with(ERROR_CODES.EMAIL_DOMAIN_NOT_ALLOWED, () => ({
					title: "Email Domain Not Allowed",
					message: "Your email domain is not authorized to submit this form.",
					secondaryMessage:
						"Please use an email address with the required domain, or contact the form creator.",
				}))
				.exhaustive(),
		[errorMessage],
	);

	return (
		<Box
			display="flex"
			justifyContent="center"
			alignItems="center"
			minHeight="100vh"
			bgcolor="background.default"
			px={2}
		>
			<Card sx={{ maxWidth: 500, textAlign: "center", p: 2 }}>
				<CardHeader
					title={title}
					titleTypographyProps={{ variant: "h5", align: "center" }}
				/>
				<CardContent>
					<Typography variant="body1" gutterBottom>
						{message}
					</Typography>
					{secondaryMessage && (
						<Typography variant="body2" color="text.secondary">
							{secondaryMessage}
						</Typography>
					)}
				</CardContent>
			</Card>
		</Box>
	);
};
